import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Accordion,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";
import {
  getHatHizList,
  updateHatHizList,
  addHatHizList,
  deleteHatHizList,
  postDolulukFormat, // API fonksiyonunu import ettik
} from "../../../api/planlama/hatHizStandart";

// Makineye özel renk ve başlık tanımları
const MACHINE_CONFIG = {
  1: { title: "1. MAKİNE HATTI", color: "primary", border: "border-primary" },
  2: { title: "2. MAKİNE HATTI", color: "success", border: "border-success" },
  3: { title: "3. MAKİNE HATTI", color: "warning", border: "border-warning" },
  4: { title: "4. MAKİNE HATTI", color: "info", border: "border-info" },
};

function HatHizStandartPage() {
  const { permissions } = useAuth();

  // --- STATE ---
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // Excel yükleme durumu
  const [showModal, setShowModal] = useState(false);

  // Edit / Delete State
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  // Form State
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState(null);

  // Ref (Gizli file input için)
  const fileInputRef = useRef(null);

  // Filtre State
  const [filters, setFilters] = useState({
    machineNo: "",
    ebat: "",
    boy: "",
    paketIciAdet: "",
    bantEni: "",
    kalinlikMin: "",
    kalinlikMax: "",
  });

  // --- VERİ ÇEKME ---
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        machineNo: filters.machineNo ? parseInt(filters.machineNo) : undefined,
        ebat: filters.ebat || undefined,
        boy: filters.boy ? parseInt(filters.boy) : undefined,
        paketIciAdet: filters.paketIciAdet
          ? parseInt(filters.paketIciAdet)
          : undefined,
        bantEni: filters.bantEni ? parseFloat(filters.bantEni) : undefined,
        kalinlikMin: filters.kalinlikMin
          ? parseFloat(filters.kalinlikMin)
          : undefined,
        kalinlikMax: filters.kalinlikMax
          ? parseFloat(filters.kalinlikMax)
          : undefined,
      };

      const res = await getHatHizList(payload);
      const data = res.data ? res.data : res;

      if (data && data.success) {
        setRows(data.data || []);
      }
    } catch (error) {
      console.error("Veri çekme hatası:", error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- EXCEL YÜKLEME VE İNDİRME İŞLEMİ ---
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // 1. API'ye dosyayı gönder
      const res = await postDolulukFormat(file);

      // 2. Gelen veriyi (Blob) indirilebilir dosyaya çevir
      // Not: Axios config'de responseType: 'blob' olması sağlıklı olur,
      // ancak veri raw geliyorsa Blob constructor ile sarmalıyoruz.
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Dosya ismini oluştur
      const fileName = `Doluluk_Raporu_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();

      // Temizlik
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel yükleme hatası:", error);
      alert("Rapor oluşturulurken bir hata meydana geldi.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Inputu sıfırla
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      machineNo: "",
      ebat: "",
      boy: "",
      paketIciAdet: "",
      bantEni: "",
      kalinlikMin: "",
      kalinlikMax: "",
    });
  };

  // --- CRUD ---
  const handleOpenAddModal = () => {
    setEditRow(null);
    setForm({
      machineNo: "",
      ebat: "",
      kalinlik: "",
      boy: "",
      paketIciAdet: "",
      hatHizi: "",
      bantEni: "",
      isActive: true,
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (row) => {
    setEditRow(row);
    setForm({ ...row });
    setFormError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setFormError(null);
    setIsLoading(true);
    try {
      const payload = {
        machineNo: parseInt(form.machineNo),
        ebat: form.ebat,
        kalinlik: parseFloat(form.kalinlik),
        boy: parseInt(form.boy),
        paketIciAdet: parseInt(form.paketIciAdet),
        hatHizi: parseFloat(form.hatHizi),
        bantEni: form.bantEni ? parseFloat(form.bantEni) : undefined,
        isActive: form.isActive,
      };

      if (!payload.machineNo || !payload.ebat || !payload.hatHizi) {
        throw new Error(
          "Lütfen zorunlu alanları (Makine No, Ebat, Hat Hızı) doldurunuz."
        );
      }

      let res;
      if (editRow) {
        res = await updateHatHizList(editRow._id, payload);
      } else {
        res = await addHatHizList(payload);
      }

      if (res && (res.success || res.data?.success)) {
        setShowModal(false);
        fetchData();
      } else {
        throw new Error(res.message || "İşlem başarısız.");
      }
    } catch (error) {
      setFormError(error.message || "Hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRow) return;
    setIsLoading(true);
    try {
      const res = await deleteHatHizList(deleteRow._id);
      if (res && (res.success || res.data?.success)) {
        setDeleteRow(null);
        fetchData();
      } else {
        alert("Silme başarısız.");
      }
    } catch (error) {
      alert("Hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- TABLE RENDER ---
  const renderMachineTable = (machineNo) => {
    const machineRows = rows.filter((r) => r.machineNo === machineNo);
    const config = MACHINE_CONFIG[machineNo];

    if (filters.machineNo && parseInt(filters.machineNo) !== machineNo)
      return null;

    return (
      <Col md={12} xl={12} className="mb-4" key={machineNo}>
        <Card className={`h-100 shadow border-0`}>
          <Card.Header
            className={`bg-white py-3 border-bottom ${config.border} border-3`}
          >
            <div className="d-flex justify-content-between align-items-center">
              <h5 className={`fw-bold text-${config.color} m-0`}>
                <i className="bi bi-gear-fill me-2"></i>
                {config.title}
              </h5>
              <Badge bg={config.color} pill>
                {machineRows.length} Kayıt
              </Badge>
            </div>
          </Card.Header>
          <div className="table-responsive">
            <Table
              hover
              striped
              bordered
              size="sm"
              className="mb-0 align-middle"
              style={{ fontSize: "11px" }}
            >
              <thead className="table-light">
                <tr className="align-middle">
                  <th className="py-2 text-center" style={{ width: "60px" }}>
                    Hız (m/dk)
                  </th>
                  <th className="py-2 text-center" style={{ width: "60px" }}>
                    Bant Eni
                  </th>
                  <th className="py-2 text-center" style={{ width: "60px" }}>
                    Kalınlık
                  </th>
                  <th className="py-2">Ebat</th>
                  <th className="py-2 text-end">Boy</th>
                  <th className="py-2 text-end">Paket</th>
                  {/* --- BACKEND HESAPLANAN ALANLAR --- */}
                  <th className="py-2 text-end bg-light text-primary">
                    Üretim (dk)
                  </th>
                  <th className="py-2 text-end bg-light text-primary">
                    Tonaj (kg)
                  </th>
                  <th className="py-2 text-end bg-light text-success">
                    Kapasite (Ton)
                  </th>
                  <th className="py-2 text-end bg-light text-success">
                    %80 Kap. (Ton)
                  </th>
                  <th className="py-2 text-center">Durum</th>
                  <th className="py-2 text-end" style={{ width: "80px" }}>
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody>
                {machineRows.length > 0 ? (
                  machineRows.map((row) => (
                    <tr key={row._id}>
                      <td className="text-center fw-bold text-primary fs-6">
                        {row.hatHizi}
                      </td>
                      <td className="text-center">{row.bantEni || "-"}</td>
                      <td className="text-center fw-bold">{row.kalinlik}</td>
                      <td className="fw-semibold text-dark">{row.ebat}</td>
                      <td className="text-end">{row.boy}</td>
                      <td className="text-end">{row.paketIciAdet}</td>

                      {/* Backend Data: Üretim Süresi */}
                      <td className="text-end fw-bold text-secondary">
                        {row.uretimDkPaket
                          ? Number(row.uretimDkPaket).toFixed(2)
                          : "-"}
                      </td>

                      {/* Backend Data: Toplam Tonaj */}
                      <td className="text-end">
                        {row.toplamTonajKg
                          ? Number(row.toplamTonajKg).toLocaleString("tr-TR", {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })
                          : "-"}
                      </td>

                      {/* Backend Data: Kapasite */}
                      <td className="text-end fw-bold text-dark">
                        {row.gunlukKapasiteTon
                          ? Number(row.gunlukKapasiteTon).toFixed(2)
                          : "-"}
                      </td>

                      {/* Backend Data: Kapasite %80 */}
                      <td className="text-end fw-bold text-success">
                        {row.gunlukKapasiteTon80
                          ? Number(row.gunlukKapasiteTon80).toFixed(2)
                          : "-"}
                      </td>

                      <td className="text-center">
                        {row.isActive ? (
                          <Badge
                            bg="success"
                            className="rounded-circle p-1"
                            style={{ width: 10, height: 10 }}
                          ></Badge>
                        ) : (
                          <Badge
                            bg="secondary"
                            className="rounded-circle p-1"
                            style={{ width: 10, height: 10 }}
                          ></Badge>
                        )}
                      </td>
                      <td className="text-end">
                        {permissions?.includes("hat_hiz_standart_update") && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-secondary me-2"
                            onClick={() => handleOpenEditModal(row)}
                            title="Düzenle"
                          >
                            <i className="bi bi-pencil-square fs-6"></i>
                          </Button>
                        )}
                        {permissions?.includes("hat_hiz_standart_delete") && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                            onClick={() => setDeleteRow(row)}
                            title="Sil"
                          >
                            <i className="bi bi-trash fs-6"></i>
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center py-3 text-muted">
                      Bu makine için kayıt bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </Col>
    );
  };

  return (
    <Layout>
      {/* --- GİZLİ INPUT --- */}
      <input
        type="file"
        accept=".xlsx, .xls"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleExcelUpload}
      />

      {/* --- HEADER --- */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <h3 className="fw-bold text-dark m-0">Üretim Hat Hız Standartları</h3>
          <small className="text-muted">
            Makine bazlı üretim hızları ve kapasite raporu.
          </small>
        </div>
        <div className="d-flex gap-2">
          {/* DOLULUK RAPORU BUTONU */}
          <Button
            variant="success"
            className="shadow-sm text-white"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                İşleniyor...
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                Doluluk Raporu (Excel)
              </>
            )}
          </Button>

          {permissions?.includes("hat_hiz_standart_post") && (
            <Button
              variant="primary"
              onClick={handleOpenAddModal}
              className="shadow-sm"
            >
              <i className="bi bi-plus-lg me-1"></i> Yeni Standart
            </Button>
          )}
        </div>
      </div>

      {/* --- FILTER --- */}
      <Accordion className="mb-4 shadow-sm" defaultActiveKey="0">
        <Accordion.Item eventKey="0" className="border-0">
          <Accordion.Header>
            <span className="fw-bold text-secondary">
              <i className="bi bi-funnel-fill me-2"></i>Filtreleme Seçenekleri
            </span>
          </Accordion.Header>
          <Accordion.Body className="bg-light">
            <Row className="g-3">
              <Col md={3}>
                <Form.Label className="small fw-bold text-muted">
                  Makine Hattı
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={filters.machineNo}
                  onChange={(e) =>
                    handleFilterChange("machineNo", e.target.value)
                  }
                >
                  <option value="">Tümü</option>
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n}. Makine
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-bold text-muted">
                  Ebat
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Örn: 40x40"
                  value={filters.ebat}
                  onChange={(e) => handleFilterChange("ebat", e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="small fw-bold text-muted">
                  Boy (mm)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  placeholder="6000"
                  value={filters.boy}
                  onChange={(e) => handleFilterChange("boy", e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="small fw-bold text-muted">
                  Kalınlık Min
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.1"
                  placeholder="Min"
                  value={filters.kalinlikMin}
                  onChange={(e) =>
                    handleFilterChange("kalinlikMin", e.target.value)
                  }
                />
              </Col>
              <Col md={2}>
                <Form.Label className="small fw-bold text-muted">
                  Kalınlık Max
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.1"
                  placeholder="Max"
                  value={filters.kalinlikMax}
                  onChange={(e) =>
                    handleFilterChange("kalinlikMax", e.target.value)
                  }
                />
              </Col>
              <Col md={12} className="text-end border-top pt-2">
                <Button
                  variant="link"
                  size="sm"
                  className="text-danger text-decoration-none me-2"
                  onClick={clearFilters}
                >
                  Temizle
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={fetchData}
                  disabled={isLoading}
                >
                  {isLoading ? "Yükleniyor..." : "Filtrele"}
                </Button>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* --- CONTENT --- */}
      {isLoading && rows.length === 0 ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Veriler yükleniyor...</p>
        </div>
      ) : (
        <Row>
          {renderMachineTable(1)}
          {renderMachineTable(2)}
          {renderMachineTable(3)}
          {renderMachineTable(4)}
        </Row>
      )}

      {/* --- MODALS --- */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editRow ? "Düzenle" : "Yeni Ekle"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label className="small fw-bold">Makine No</Form.Label>
                <Form.Select
                  value={form.machineNo}
                  onChange={(e) =>
                    setForm({ ...form, machineNo: e.target.value })
                  }
                >
                  <option value="">Seç...</option>
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n}. Makine
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={8}>
                <Form.Label className="small fw-bold">Ebat</Form.Label>
                <Form.Control
                  type="text"
                  value={form.ebat}
                  placeholder="Örn: 40x40"
                  onChange={(e) => setForm({ ...form, ebat: e.target.value })}
                />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold">Kalınlık (mm)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={form.kalinlik}
                  onChange={(e) =>
                    setForm({ ...form, kalinlik: e.target.value })
                  }
                />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold">Boy (mm)</Form.Label>
                <Form.Control
                  type="number"
                  value={form.boy}
                  onChange={(e) => setForm({ ...form, boy: e.target.value })}
                />
              </Col>
              <Col md={4}>
                <Form.Label className="small fw-bold">Paket İçi</Form.Label>
                <Form.Control
                  type="number"
                  value={form.paketIciAdet}
                  onChange={(e) =>
                    setForm({ ...form, paketIciAdet: e.target.value })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold text-primary">
                  Hat Hızı (m/dk)
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  className="border-primary fw-bold"
                  value={form.hatHizi}
                  onChange={(e) =>
                    setForm({ ...form, hatHizi: e.target.value })
                  }
                />
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold">Bant Eni</Form.Label>
                <Form.Control
                  type="number"
                  value={form.bantEni}
                  onChange={(e) =>
                    setForm({ ...form, bantEni: e.target.value })
                  }
                />
              </Col>
              <Col md={12}>
                <Form.Check
                  type="switch"
                  label="Aktif Kayıt"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              "Kaydet"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!deleteRow}
        onHide={() => setDeleteRow(null)}
        centered
        size="sm"
      >
        <Modal.Body className="text-center p-4">
          <i className="bi bi-exclamation-triangle text-danger display-4 mb-3"></i>
          <p className="fw-bold mb-1">Silmek istediğinize emin misiniz?</p>
          <small className="text-muted d-block mb-3">
            Bu işlem geri alınamaz.
          </small>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeleteRow(null)}
            >
              Hayır
            </Button>
            <Button variant="danger" size="sm" onClick={handleDeleteConfirm}>
              Evet, Sil
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Layout>
  );
}

export default HatHizStandartPage;
